import { useCallback, useMemo, useState, type ReactNode } from 'react'
import { Link, useSearchParams } from 'react-router'
import { api } from '../../servizi/api.ts'
import { useSessione } from '../../servizi/sessione.tsx'
import { Attesa, Avviso, Vuoto } from '../../componenti/Pezzi.tsx'
import {
  AreaTesto, Bottone, Etichetta, Modale, Paginazione, Riquadro, Scelta, Tabella, type TonoEtichetta,
} from '../../componenti/base.tsx'
import { LIMITE, giorno, messaggioErrore, usaElenco } from '../../componenti/elenco.tsx'

/**
 * Decisioni sui dati migrati (legacy.decisione), una per una (461).
 *
 * Il gestionale non sa eseguirne nessuna da solo, tranne gli abbinamenti delle sedi CIDIS
 * registrate come enti, che hanno la loro sezione: per ogni tipo la pagina dice che cosa fare a
 * mano e permette di segnare la decisione come presa, con una nota. Per i doppioni la scelta
 * (stessa persona, scheda da tenere) si registra in modo strutturato, e da qui chi ha persona.unisci
 * apre l'unione delle schede (473) sulla scheda da tenere. Le schede si mostrano solo a chi le legge,
 * e solo chi le legge tutte decide.
 */

type Persona = { id: string; etichetta: string; numero_scheda: number | null; in_chiaro: boolean }

type Decisione = {
  id: string; tipo: string; stato: string; domanda: string | null
  dettagli: Record<string, unknown>
  persone: Persona[]; persone_nascoste: number; riservata: boolean; puo_decidere: boolean
  nota: string | null; creata_il: string; decisa_il: string | null; decisa_da: string | null
}

/** Nome di ogni tipo e che cosa fare a mano: e' il percorso guidato della decisione. */
const TIPI: Record<string, { nome: string; cosaFare: string }> = {
  doppione: {
    nome: 'Possibili doppioni',
    cosaFare: 'Apri le schede e confronta i dati (documento, data di nascita, percorsi). Se sono la stessa '
      + 'persona, indica quale scheda tenere e uniscile con «Unisci le schede» (Direzione): tutto passa alla '
      + 'scheda tenuta e l’altra resta come rimando. Se sono persone diverse, indicalo: le schede restano come sono.',
  },
  percorso_aperto: {
    nome: 'Percorsi senza data di fine',
    cosaFare: 'Il vecchio archivio non aveva la data di fine di questo percorso. Se è ancora in corso, scegli '
      + '«Resta aperto». Se è finito, apri la scheda, linguetta Percorsi, chiudilo con «Chiudi percorso» '
      + 'indicando la data, e poi segna la decisione come presa.',
  },
  sede_persona: {
    nome: 'Persone seguite da più sedi',
    cosaFare: 'La persona è stata seguita da più sedi. Se la sede di riferimento indicata nella scheda va bene, '
      + 'scegli «Va bene così». Altrimenti cambiala nella scheda, linguetta Anagrafica, e poi segna la '
      + 'decisione come presa.',
  },
  mappatura_servizio: {
    nome: 'Servizi senza corrispondenza',
    cosaFare: 'Scrivi nella nota in quale area e tipologia rientra il servizio. Honey & Molly aggiorna la '
      + 'tabella di corrispondenza e ripete l’importazione: fino ad allora i percorsi di questo servizio '
      + 'restano fuori.',
  },
  altro: {
    nome: 'Enti dei progetti',
    cosaFare: 'Apri il progetto e collega capofila, attuatore e partner agli enti dell’anagrafica (se un ente '
      + 'non c’è, crealo dalla pagina Enti); poi segna la decisione come presa.',
  },
}

const TIPO_GENERICO = {
  nome: 'Altre decisioni',
  cosaFare: 'Leggi la domanda, fai a mano quello che serve e segna la decisione come presa, spiegando nella '
    + 'nota che cosa hai fatto.',
}

const tipoDi = (tipo: string) => TIPI[tipo] ?? TIPO_GENERICO

const STATO: Record<string, { nome: string; tono: TonoEtichetta }> = {
  da_confermare: { nome: 'da prendere', tono: 'attenzione' },
  rinviata: { nome: 'rinviata', tono: 'neutro' },
  confermata: { nome: 'presa', tono: 'positivo' },
  modificata: { nome: 'presa', tono: 'positivo' },
}

const FILTRO_STATO = [
  { valore: 'da_confermare', etichetta: 'Da prendere' },
  { valore: 'rinviata', etichetta: 'Rinviate' },
  { valore: 'decise', etichetta: 'Prese' },
  { valore: 'tutte', etichetta: 'Tutte' },
]

const testo = (v: unknown) => (typeof v === 'string' && v.trim() ? v : null)

/** Che cosa dice la decisione, per tipo, con le parole della pagina e non quelle della proposta. */
function Descrizione({ d }: { d: Decisione }) {
  const { puo } = useSessione()
  const x = d.dettagli
  let corpo: ReactNode
  switch (d.tipo) {
    case 'doppione':
      corpo = <>
        {x.schede ? `${String(x.schede)} schede` : 'Più schede'}
        {testo(x.motivo) ? `: ${testo(x.motivo)}.` : '.'}
        {x.esito === 'stessa_persona' && <> Decisa: <strong>stessa persona</strong>.</>}
        {x.esito === 'persone_diverse' && <> Decisa: <strong>persone diverse</strong>.</>}
      </>
      break
    case 'percorso_aperto': {
      const percorso = x.percorso as { stato: string; data_chiusura: string | null } | null
      corpo = <>
        Percorso «{testo(x.servizio) ?? 'servizio non indicato'}»
        {testo(x.aperto_dal) ? `, aperto dal ${giorno(testo(x.aperto_dal))}` : ''}, senza data di fine.
        {percorso && (percorso.stato === 'chiuso'
          ? <> Oggi è chiuso{percorso.data_chiusura ? ` (dal ${giorno(percorso.data_chiusura)})` : ''}.</>
          : <> Oggi è {percorso.stato}.</>)}
      </>
      break
    }
    case 'sede_persona':
      corpo = <>
        Seguita da {typeof x.sedi_diverse === 'number' ? `${x.sedi_diverse} sedi diverse` : 'più sedi'}.
        {testo(x.sede_attuale) && <> Oggi la sede di riferimento è <strong>{testo(x.sede_attuale)}</strong>.</>}
      </>
      break
    case 'mappatura_servizio':
      corpo = <>
        Servizio {x.per === 'enti' ? 'agli enti' : 'alle persone'} «{testo(x.servizio) ?? 'senza descrizione'}».
      </>
      break
    case 'altro': {
      const progetto = x.progetto as { id: string | null; titolo: string } | null
      const enti = [['capofila', x.capofila], ['attuatore', x.attuatore], ['partner', x.partner]]
        .filter(([, v]) => testo(v)).map(([k, v]) => `${k} «${v as string}»`)
      corpo = <>
        {progetto
          ? <>Progetto {progetto.id && puo('progetto.leggi')
              ? <Link to={`/progetti/${progetto.id}`}>«{progetto.titolo}»</Link> : `«${progetto.titolo}»`}</>
          : 'Progetto'}
        {enti.length ? `: ${enti.join(', ')}.` : '.'}
      </>
      break
    }
    default:
      corpo = d.domanda ?? 'Decisione senza descrizione.'
  }
  return <span>{corpo}</span>
}

export function DecisioniDaPrendere() {
  const [parametri, setParametri] = useSearchParams()
  const tipo = parametri.get('tipo_decisione') ?? ''
  const statoGrezzo = parametri.get('stato_decisione') ?? ''
  const stato = FILTRO_STATO.some((s) => s.valore === statoGrezzo) ? statoGrezzo : 'da_confermare'

  const aggiorna = useCallback((valori: Record<string, string>) => {
    setParametri((attuali) => {
      const nuovi = new URLSearchParams(attuali)
      for (const [chiave, valore] of Object.entries(valori)) {
        if (valore) nuovi.set(chiave, valore); else nuovi.delete(chiave)
      }
      return nuovi
    }, { replace: true })
  }, [setParametri])

  const percorso = useMemo(() => {
    const q = new URLSearchParams({ stato })
    if (tipo) q.set('tipo', tipo)
    return `/migrazione/decisioni?${q.toString()}`
  }, [stato, tipo])
  const { dati, errore, finestra, ricarica } = usaElenco<Decisione>(percorso)

  const [inCorso, setInCorso] = useState<string | null>(null)
  const [erroreComando, setErroreComando] = useState<string | null>(null)
  const [daDecidere, setDaDecidere] = useState<Decisione | null>(null)

  async function decidi(d: Decisione, corpo: { stato: string; scelta?: Record<string, unknown>; nota?: string }) {
    setInCorso(d.id); setErroreComando(null)
    try {
      await api.post(`/migrazione/decisioni/${d.id}`, corpo)
      setDaDecidere(null)
      ricarica()
    } catch (e) {
      setErroreComando(messaggioErrore(e))
    } finally { setInCorso(null) }
  }

  return (
    <>
      <Riquadro titolo="Decisioni da prendere" azioni={tipo || stato !== 'da_confermare'
        ? <Bottone variante="discreto" dimensione="piccolo"
                   onClick={() => aggiorna({ tipo_decisione: '', stato_decisione: '' })}>Azzera i filtri</Bottone>
        : undefined}>
        <p className="tenue">
          Domande che la migrazione non poteva risolvere da sola. Il gestionale non le esegue: per ognuna
          la pagina dice che cosa fare, e dopo si segna la decisione come presa.
        </p>
        <div className="filtri-report">
          <Scelta etichetta="Tipo" valore={tipo} cambia={(v) => aggiorna({ tipo_decisione: v })} vuota="Tutti i tipi"
                  opzioni={Object.entries(TIPI).map(([valore, t]) => ({ valore, etichetta: t.nome }))} />
          <Scelta etichetta="Stato" valore={stato}
                  cambia={(v) => aggiorna({ stato_decisione: v === 'da_confermare' ? '' : v })} opzioni={FILTRO_STATO} />
        </div>
        {tipo && (
          <div className="migrazione-spiegazione">
            <p><strong>Che cosa fare.</strong> {tipoDi(tipo).cosaFare}</p>
          </div>
        )}
      </Riquadro>

      {(errore || erroreComando) && <Avviso tipo="errore">{errore ?? erroreComando}</Avviso>}
      {!errore && !dati && <Attesa />}
      {dati && (
        <Riquadro denso>
          <Tabella intestazioni={['Decisione', 'Di che cosa si tratta', 'Stato',
                     <span key="comandi" className="solo-lettore">Comandi</span>]}
                   vuoto={<Vuoto testo={stato === 'da_confermare' ? 'Nessuna decisione da prendere con questi filtri.' : 'Nessuna decisione con questi filtri.'} />}>
            {dati.map((d) => {
              const aperta = d.stato === 'da_confermare' || d.stato === 'rinviata'
              const sedeAttuale = testo(d.dettagli.sede_attuale)
              return (
                <tr key={d.id}>
                  <td data-etichetta="Decisione">
                    <span className="dato-testo">
                      <span className="voce-principale">{tipoDi(d.tipo).nome}</span>
                      <span className="piccolo tenue">dal {giorno(d.creata_il)}</span>
                    </span>
                  </td>
                  <td data-etichetta="Di che cosa si tratta">
                    <span className="dato-testo">
                      <Descrizione d={d} />
                      {d.persone.map((p) => (
                        <span key={p.id} className="piccolo">
                          <Link to={`/persone/${p.id}`}>{p.etichetta}</Link>
                          {p.numero_scheda !== null && <span className="tenue"> · scheda {p.numero_scheda}</span>}
                        </span>
                      ))}
                      {d.persone_nascoste > 0 && (
                        <span className="piccolo tenue">
                          {d.persone_nascoste === 1
                            ? 'Una scheda non rientra nei tuoi incarichi.'
                            : `${d.persone_nascoste} schede non rientrano nei tuoi incarichi.`}
                        </span>
                      )}
                      {aperta && !d.puo_decidere && d.riservata && (
                        <span className="piccolo tenue">Per decidere servono le schede: se ne occupa chi le segue.</span>
                      )}
                    </span>
                  </td>
                  <td data-etichetta="Stato">
                    <span className="dato-testo">
                      <span><Etichetta tono={STATO[d.stato]?.tono ?? 'neutro'}>{STATO[d.stato]?.nome ?? d.stato}</Etichetta></span>
                      {d.decisa_il && (
                        <span className="piccolo tenue">{giorno(d.decisa_il)}{d.decisa_da ? `, ${d.decisa_da}` : ''}</span>
                      )}
                      {d.nota && <span className="piccolo migrazione-nota">{d.nota}</span>}
                    </span>
                  </td>
                  <td data-etichetta="Comandi">
                    {d.puo_decidere && aperta && (
                      <div className="riga">
                        {d.tipo === 'percorso_aperto' && (
                          <Bottone variante="secondario" dimensione="piccolo" disabled={inCorso === d.id}
                                   onClick={() => void decidi(d, {
                                     stato: 'confermata', scelta: { esito: 'resta_aperto' },
                                     nota: 'Il percorso resta aperto.',
                                   })}>
                            Resta aperto
                          </Bottone>
                        )}
                        {d.tipo === 'sede_persona' && sedeAttuale && (
                          <Bottone variante="secondario" dimensione="piccolo" disabled={inCorso === d.id}
                                   onClick={() => void decidi(d, {
                                     stato: 'confermata', scelta: { esito: 'sede_attuale' },
                                     nota: `Va bene la sede di riferimento indicata nella scheda: ${sedeAttuale}.`,
                                   })}>
                            Va bene così
                          </Bottone>
                        )}
                        <Bottone variante="primario" dimensione="piccolo" disabled={inCorso === d.id}
                                 onClick={() => setDaDecidere(d)}>
                          Segna come presa
                        </Bottone>
                        {d.stato === 'da_confermare' && (
                          <Bottone variante="discreto" dimensione="piccolo" disabled={inCorso === d.id}
                                   onClick={() => void decidi(d, { stato: 'rinviata' })}>
                            Rinvia
                          </Bottone>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              )
            })}
          </Tabella>
          <Paginazione salta={finestra.salta} limite={LIMITE} totale={finestra.totale}
                       ricevute={dati.length} cambia={finestra.cambia} />
        </Riquadro>
      )}

      {daDecidere && (
        <ModaleDecisione d={daDecidere} inCorso={inCorso === daDecidere.id}
                         annulla={() => setDaDecidere(null)}
                         conferma={(corpo) => void decidi(daDecidere, { stato: 'confermata', ...corpo })} />
      )}
    </>
  )
}

/**
 * Percorso guidato di una decisione: che cosa fare, la scelta (per i doppioni) e la nota.
 * La nota e' obbligatoria: e' l'unica traccia di che cosa e' stato fatto a mano.
 */
function ModaleDecisione({ d, inCorso, annulla, conferma }: {
  d: Decisione; inCorso: boolean; annulla: () => void
  conferma: (corpo: { scelta?: Record<string, unknown>; nota: string }) => void
}) {
  const doppione = d.tipo === 'doppione'
  const { puo } = useSessione()
  const [esito, setEsito] = useState('')
  const [daTenere, setDaTenere] = useState('')
  const [nota, setNota] = useState('')
  const [errore, setErrore] = useState<string | null>(null)

  const invia = () => {
    if (doppione && !esito) { setErrore('Indica se sono la stessa persona.'); return }
    if (doppione && esito === 'stessa_persona' && !daTenere) { setErrore('Indica quale scheda tenere.'); return }
    if (nota.trim().length < 3) { setErrore('Scrivi nella nota che cosa è stato deciso o fatto.'); return }
    conferma({
      scelta: doppione
        ? { esito, ...(esito === 'stessa_persona' ? { scheda_da_tenere: daTenere } : {}) }
        : { esito: 'fatto_a_mano' },
      nota: nota.trim(),
    })
  }

  return (
    <Modale titolo={tipoDi(d.tipo).nome} chiudi={annulla} larga
            azioni={
              <>
                <Bottone variante="discreto" onClick={annulla}>Annulla</Bottone>
                <Bottone variante="primario" inCorso={inCorso} onClick={invia}>Segna come presa</Bottone>
              </>
            }>
      <p><Descrizione d={d} /></p>
      {d.persone.length > 0 && (
        <ul className="elenco-avviso">
          {d.persone.map((p) => (
            <li key={p.id}>
              <Link to={`/persone/${p.id}`} target="_blank" rel="noreferrer">{p.etichetta}</Link>
              {p.numero_scheda !== null && <span className="tenue"> · scheda {p.numero_scheda}</span>}
            </li>
          ))}
        </ul>
      )}
      <Avviso><strong>Che cosa fare.</strong> {tipoDi(d.tipo).cosaFare}</Avviso>
      {doppione && (
        <>
          <Scelta etichetta="Sono la stessa persona?" valore={esito} obbligatorio
                  cambia={(v) => { setEsito(v); setErrore(null) }} vuota="— scegli —"
                  opzioni={[
                    { valore: 'stessa_persona', etichetta: 'Sì, è la stessa persona' },
                    { valore: 'persone_diverse', etichetta: 'No, sono persone diverse' },
                  ]} />
          {esito === 'stessa_persona' && (
            <Scelta etichetta="Scheda da tenere" valore={daTenere} obbligatorio
                    cambia={(v) => { setDaTenere(v); setErrore(null) }} vuota="— scegli la scheda —"
                    opzioni={d.persone.map((p) => ({
                      valore: p.id, etichetta: `${p.etichetta}${p.numero_scheda !== null ? ` · scheda ${p.numero_scheda}` : ''}`,
                    }))} />
          )}
          {/* «Unisci schede» (473): dalla scheda da tenere, con l'altra gia' scelta. */}
          {esito === 'stessa_persona' && daTenere && puo('persona.unisci') && (
            <ul className="elenco-avviso">
              {d.persone.filter((p) => p.id !== daTenere).map((p) => (
                <li key={p.id}>
                  <Link to={`/persone/${daTenere}?unisci=${p.id}`} target="_blank" rel="noreferrer">
                    Unisci la scheda {p.numero_scheda ?? ''} a quella da tenere
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
      <AreaTesto etichetta="Nota" valore={nota} obbligatorio maxLength={1000} errore={errore}
                 cambia={(v) => { setNota(v); setErrore(null) }}
                 aiuto={d.tipo === 'mappatura_servizio'
                   ? 'Area e tipologia in cui rientra il servizio.'
                   : 'Che cosa è stato deciso o fatto, per chi verrà dopo.'} />
    </Modale>
  )
}
